import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { ZodError } from 'zod';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { ErrorCard, InlineNotice, NotConfiguredState, PageHeader, Skeleton, notConfiguredText, } from '@wit-support/ui';
import { Time, actorLabelRu, formatInZone, useAdminTimezone } from '@wit-support/ui-domain';
import { FlaskConical, MessageSquareOff, OctagonX } from 'lucide-react';
import { listConnections } from './api.js';
/** Корень ключей раздела — одна инвалидация после мутации обновляет все экраны. */
export const SOCIAL_KEY = ['social'];
export const CONNECTIONS_KEY = ['social', 'connections'];
export const SETTINGS_KEY = ['social', 'settings'];
export const POLICIES_KEY = ['social', 'policies'];
const ENGINE_SERVICE = 'Контент-движок';
const ENGINE_ENV = ['CONTENT_ENGINE_API_BASE_URL', 'CONTENT_ENGINE_API_TOKEN'];
export const NOT_CONFIGURED_TEXT = notConfiguredText(ENGINE_SERVICE, ENGINE_ENV);
export const UNAVAILABLE_TEXT = 'Движок недоступен, попробуйте позже';
const SOCIAL_MODULE_SERVICE = 'Модуль комментариев';
const SOCIAL_MODULE_ENV = ['ENGINE_SOCIAL=on'];
const SOCIAL_MODULE_APP = 'движке wit_video_combine';
/** «Модуль комментариев не подключён: задайте ENGINE_SOCIAL=on в движке wit_video_combine». */
export const SOCIAL_DISABLED_TEXT = notConfiguredText(SOCIAL_MODULE_SERVICE, SOCIAL_MODULE_ENV, SOCIAL_MODULE_APP);
/**
 * «Подключения» при `enabled:false`, но с ключом шифрования: подключение
 * токенов и их продление в движке от `ENGINE_SOCIAL` не зависят (RULINGS-FINAL
 * M-2) — выключены только комментарии.
 */
export const COMMENTS_OFF_CONNECTIONS_WORK_TEXT = 'Комментарии выключены в движке — подключения и продление токенов работают';
const SECRETS_SERVICE = 'Ключ шифрования токенов';
const SECRETS_ENV = ['ENGINE_SECRETS_KEY'];
/** «Ключ шифрования токенов не подключён: задайте ENGINE_SECRETS_KEY в движке wit_video_combine». */
export const SECRETS_NOT_CONFIGURED_TEXT = notConfiguredText(SECRETS_SERVICE, SECRETS_ENV, SOCIAL_MODULE_APP);
export const DRY_RUN_TEXT = 'Сухой режим: действия отмечаются выполненными, но в сеть не уходят';
export const FORBIDDEN_READ_TEXT = 'Раздел «Соцсети» недоступен вашей роли: его видят участники команды, супервайзеры и владелец.';
export const FORBIDDEN_WRITE_TEXT = 'Действия с комментариями и настройки реагирования доступны супервайзеру и владельцу.';
export const FORBIDDEN_CONNECTIONS_TEXT = 'Подключать и отключать токены может только владелец.';
/** 404 admin-api без кода движка на запись (`PUT /settings`: строки уже нет). */
export const WRITE_NOT_FOUND_TEXT = 'Такого аккаунта или сети в движке уже нет — обновите страницу.';
/** 2xx не по контракту admin-api (разбор схемой упал) — при загрузке. */
export const READ_CONTRACT_TEXT = 'Не удалось загрузить: ответ пришёл в неожиданном формате. Обновите страницу; если повторится — сообщите разработчикам.';
/** То же на запись: запрос прошёл (2xx), действие могло выполниться. */
export const WRITE_CONTRACT_TEXT = 'Запрос отправлен, но ответ пришёл в неожиданном формате — обновите страницу и проверьте результат.';
/**
 * Свои фразы для кодов движка (`upstreamCode`), где экрану нужно сказать
 * больше или иначе, чем общая фраза admin-api. Остальные коды — фраза
 * admin-api (`serverMessage`).
 */
const UPSTREAM_TEXT_RU = {
    social_not_enabled: SOCIAL_DISABLED_TEXT,
    social_secrets_not_configured: 'В движке не задан ключ шифрования — подключение токенов недоступно.',
    social_token_invalid: 'Сеть не приняла токен: он неверный, истёк или выдан без нужных прав (токену Facebook обязательно нужно право instagram_manage_comments). Получите новый токен и вставьте его снова.',
    social_token_mismatch: 'Токен принадлежит другому аккаунту: имя в сети не совпадает с этим аккаунтом. Проверьте, под каким профилем выдан токен.',
    social_token_no_instagram: 'Токен не видит ни одной Страницы Facebook с привязанным Instagram. Системному пользователю назначьте Страницу этого аккаунта (Business Settings → Системные пользователи → Назначить ресурсы) или привяжите Instagram к Странице — и вставьте токен снова.',
    social_connection_missing: 'Подключения уже нет — обновите страницу.',
    social_network_error: 'Сеть не ответила. Попробуйте ещё раз через пару минут.',
    network_unsupported: 'Для этой сети реагирование на комментарии ещё не сделано.',
    social_action_exists: 'Такое действие уже есть — оно в очереди или выполнено. Повторить можно только после ошибки или пропуска.',
    social_action_unsupported: 'Это действие недоступно при текущем подключении.',
    social_action_not_pending: 'Предложение уже обработано — обновите страницу.',
    social_poll_running: 'Опрос уже идёт — новые комментарии появятся через пару минут.',
};
/**
 * Фразы для кодов движка на паре сети Facebook (Страница): где проверять
 * ограничение, какие права нужны и какая Страница — своё. admin-api отдаёт
 * фразу без сети (про Instagram), поэтому экран, знающий сеть, берёт эту.
 */
export const FB_UPSTREAM_TEXT_RU = {
    social_token_invalid: 'Сеть не приняла токен: он неверный, отозван или выдан без нужных прав (для Страницы обязательно право pages_read_user_content). Вставьте токен системного пользователя «WIT Social» заново.',
    social_token_mismatch: 'Токен не видит Страницу Facebook этого аккаунта: среди Страниц системного пользователя нет той, к которой привязан этот Instagram. Назначьте Страницу системному пользователю (Business Settings → Системные пользователи → Назначить ресурсы) и повторите.',
    social_account_blocked: 'Facebook ограничил эту Страницу — действия не принимаются и режим не включается, пока идёт пауза после ограничения или пока владелец не нажмёт «Возобновить» (сначала проверьте Страницу в Facebook или в Meta Business Suite). Не повторяйте действия, пока ограничение не снято.',
    social_block_confirm_required: 'Страница на паузе после ограничения Facebook — подтвердите, что ограничение снято: проверьте Страницу в Facebook или в Meta Business Suite и только потом нажмите «Возобновить» ещё раз.',
};
/** Фраза для известного кода движка, иначе null (машинный код человеку не показываем). */
export function socialCodeText(code) {
    return UPSTREAM_TEXT_RU[code] ?? null;
}
/** Ошибка — код admin-api / движка, если это `ModuleRequestError`. */
export function upstreamCodeOf(err) {
    return err instanceof ModuleRequestError ? err.upstreamCode : null;
}
export function isSocialDisabledError(err) {
    return upstreamCodeOf(err) === 'social_not_enabled';
}
export function isEngineNotConfiguredError(err) {
    return err instanceof ModuleRequestError && err.code === 'social_engine_not_configured';
}
/**
 * Ошибку — словами. Решение по кодам (`status`, `code`, `upstreamCode`), не
 * по тексту. `mode`: `read` — загрузка; `write` — действие или настройка;
 * `connections` — подключение токенов (там 403 значит «только владелец»).
 * `network` — сеть пары, если экран её знает: у Facebook свои фразы для
 * токена и ограничения Страницы.
 */
export function describeSocialError(err, mode = 'read', network) {
    const prefix = mode === 'read' ? 'Не удалось загрузить' : 'Не удалось выполнить';
    if (err instanceof ModuleRequestError) {
        if (err.status === 403) {
            if (mode === 'read')
                return FORBIDDEN_READ_TEXT;
            return mode === 'connections' ? FORBIDDEN_CONNECTIONS_TEXT : FORBIDDEN_WRITE_TEXT;
        }
        if (err.code === 'social_engine_not_configured')
            return NOT_CONFIGURED_TEXT;
        const fb = network === 'facebook' && err.upstreamCode !== null
            ? FB_UPSTREAM_TEXT_RU[err.upstreamCode]
            : undefined;
        if (fb)
            return fb;
        const own = err.upstreamCode !== null ? UPSTREAM_TEXT_RU[err.upstreamCode] : undefined;
        if (own)
            return own;
        if (err.serverMessage)
            return err.serverMessage;
        if (mode !== 'read' && err.status === 404 && err.code === 'not_found') {
            return WRITE_NOT_FOUND_TEXT;
        }
        if (err.code === 'upstream_unavailable' ||
            err.status === 502 ||
            err.status === 503 ||
            err.status === 504) {
            return UNAVAILABLE_TEXT;
        }
        return `${prefix}: ${err.code}`;
    }
    // Текст zod — английский и технический; человеку он ничего не скажет.
    if (err instanceof ZodError)
        return mode === 'read' ? READ_CONTRACT_TEXT : WRITE_CONTRACT_TEXT;
    const message = err instanceof Error ? err.message : String(err);
    return `${prefix}: ${message}`;
}
/**
 * Сабмит диалога через `mutateAsync`: `ZodForm` ждёт промис (кнопка
 * «Сохраняем…» заблокирована), ошибку уже показал `onError` мутации.
 */
export async function submitting(p) {
    await p.catch(() => undefined);
}
/** Подключения + флаги модуля (`enabled`, `dryRun`, `secretsConfigured`). */
export function useSocialConnections() {
    return useQuery({ queryKey: CONNECTIONS_KEY, queryFn: listConnections });
}
/**
 * Откуда признак сухого режима (FX-SE3 L3): движок отдаёт его по последнему
 * тику исполнителя engine — тогда «по данным движка на HH:MM»; пока engine не
 * тикал — по настройке процесса admin (может не совпадать с engine).
 */
export function dryRunSourceText(source, reportedAt, fmt) {
    if (source === 'engine' && reportedAt)
        return `по данным движка на ${fmt(reportedAt)}`;
    if (source === 'admin_env')
        return 'движок ещё не отчитался — по настройке админки';
    return null;
}
/** Баннер сухого режима — на всех трёх страницах (SPEC §1.8). */
export function DryRunBanner({ dryRun, source = null, reportedAt = null, }) {
    const tz = useAdminTimezone();
    if (!dryRun)
        return null;
    const from = dryRunSourceText(source, reportedAt, (v) => formatInZone(v, 'HH:mm', tz));
    return (_jsxs(InlineNotice, { variant: "info", bordered: true, icon: FlaskConical, children: [DRY_RUN_TEXT, from ? ` (${from})` : ''] }));
}
/**
 * «Все действия остановлены (ops@…, 10.10 08:00)» — общий стоп движка
 * (RULINGS-SAFETY): плашка на всех экранах раздела, чтобы никто не гадал,
 * почему очередь стоит.
 */
export function GlobalStopBanner({ globalStop }) {
    if (!globalStop?.stopped)
        return null;
    const who = actorLabelRu(globalStop.by);
    return (_jsxs(InlineNotice, { variant: "danger", bordered: true, icon: OctagonX, role: "status", children: [_jsx("span", { className: "font-medium", children: "\u0412\u0441\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044F \u043E\u0441\u0442\u0430\u043D\u043E\u0432\u043B\u0435\u043D\u044B" }), who || globalStop.at ? (_jsxs(_Fragment, { children: [' ', "(", who ?? '', who && globalStop.at ? ', ' : '', globalStop.at ? _jsx(Time, { iso: globalStop.at, preset: "shortNumeric" }) : null, ")"] })) : null, globalStop.reason ? `: ${globalStop.reason}` : '', ". \u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0438 \u0441\u043E\u0431\u0438\u0440\u0430\u044E\u0442\u0441\u044F, \u043D\u043E \u0441\u0435\u0440\u0434\u0435\u0447\u043A\u0438, \u043E\u0442\u0432\u0435\u0442\u044B, \u0441\u043A\u0440\u044B\u0442\u0438\u0435 \u0438 \u0443\u0434\u0430\u043B\u0435\u043D\u0438\u0435 \u0432 \u0441\u043E\u0446\u0441\u0435\u0442\u0438 \u043D\u0435 \u0443\u0445\u043E\u0434\u044F\u0442, \u043F\u043E\u043A\u0430 \u0432\u043B\u0430\u0434\u0435\u043B\u0435\u0446 \u043D\u0435 \u043D\u0430\u0436\u043C\u0451\u0442 \u00AB\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u0432\u0441\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044F\u00BB."] }));
}
/**
 * «Модуль выключен в движке» (`enabled:false` или 409 `social_not_enabled`) —
 * состояние, а не ошибка: тот же `NotConfiguredState`, что «движок не
 * подключён», только про флаг движка.
 */
export function SocialDisabledState() {
    return (_jsx(NotConfiguredState, { service: SOCIAL_MODULE_SERVICE, envVars: SOCIAL_MODULE_ENV, app: SOCIAL_MODULE_APP }));
}
/**
 * «Подключения» без ключа шифрования в движке при выключенных комментариях:
 * на странице не работает ничего — спокойное состояние «не подключён».
 */
export function SecretsNotConfiguredState() {
    return (_jsx(NotConfiguredState, { service: SECRETS_SERVICE, envVars: SECRETS_ENV, app: SOCIAL_MODULE_APP }));
}
/**
 * Состояние ошибки экрана: «движок не подключён к admin-api» и «модуль
 * выключен в движке» — спокойные состояния, остальное — `ErrorCard`.
 */
export function SocialErrorState({ error }) {
    if (isEngineNotConfiguredError(error)) {
        return _jsx(NotConfiguredState, { service: ENGINE_SERVICE, envVars: ENGINE_ENV });
    }
    if (isSocialDisabledError(error))
        return _jsx(SocialDisabledState, {});
    return _jsx(ErrorCard, { children: describeSocialError(error) });
}
/**
 * Каркас экрана: заголовок + проверка «движок подключён, модуль включён» по
 * `GET /connections` (он отвечает и при выключенном модуле — `enabled:false`,
 * без пробных 409) + баннер сухого режима. Тело монтируется только при
 * `enabled:true`, иначе каждый экран показал бы свою ошибку 409. Исключение —
 * `worksWithCommentsOff` («Подключения»): при `enabled:false` и ключе
 * шифрования тело монтируется с инфо-плашкой.
 */
export function SocialScreen({ title, description, actions, worksWithCommentsOff = false, children, }) {
    const q = useSocialConnections();
    const commentsOff = worksWithCommentsOff && q.data?.enabled === false && q.data.secretsConfigured;
    const ready = q.data?.enabled === true || commentsOff;
    return (_jsxs("div", { className: "mx-auto flex max-w-[1200px] flex-col gap-6 p-4 sm:p-6 lg:p-8", children: [_jsx(PageHeader, { title: title, description: description, actions: ready ? actions : null }), q.isError ? (_jsx(SocialErrorState, { error: q.error })) : !q.data ? (_jsx(Skeleton, { className: "h-32 w-full" })) : commentsOff ? (_jsxs(_Fragment, { children: [_jsx(InlineNotice, { variant: "info", bordered: true, icon: MessageSquareOff, children: COMMENTS_OFF_CONNECTIONS_WORK_TEXT }), _jsx(GlobalStopBanner, { globalStop: q.data.globalStop }), children(q.data)] })) : !q.data.enabled ? (worksWithCommentsOff ? (_jsx(SecretsNotConfiguredState, {})) : (_jsx(SocialDisabledState, {}))) : (_jsxs(_Fragment, { children: [_jsx(GlobalStopBanner, { globalStop: q.data.globalStop }), _jsx(DryRunBanner, { dryRun: q.data.dryRun, source: q.data.dryRunSource, reportedAt: q.data.dryRunReportedAt }), children(q.data)] }))] }));
}
/** Уникальные аккаунты из таблицы подключений, по алфавиту. */
export function accountsOf(status) {
    return [...new Set(status.items.map((c) => c.account))].sort((a, b) => a.localeCompare(b));
}
//# sourceMappingURL=shared.js.map